<script setup lang="ts">
import {
  OperatorBreadcrumbDataQuery,
  OperatorBreadcrumbDataQueryVariables,
} from '@/generated/graphql'
import { useRoute, useRouter } from 'vue-router'
import { useApolloClient, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import BreadcrumbTrail, { BreadcrumbItem } from '@/components/BreadcrumbTrail.vue'
import { itemSortByName } from '@/app'
import { cachedBopVersion, cachedEntityName } from '@/utils/breadcrumbCache'
import useCurrentProductionBatch from '@/components/operator/currentProductionBatch'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { client } = useApolloClient()

const productId = computed(() => route.params.productId as string | undefined)
const configId = computed(() => route.params.productConfigId as string | undefined)
const siteId = computed(() => route.params.siteId as string | undefined)
const bopId = computed(() => route.params.bopId as string | undefined)
const moduleId = computed(() => route.params.moduleId as string | undefined)

const fetchQuery = useQuery<OperatorBreadcrumbDataQuery, OperatorBreadcrumbDataQueryVariables>(
  gql`
    query OperatorBreadcrumbData(
      $productId: ID!
      $includeProduct: Boolean!
      $configId: ID!
      $includeConfig: Boolean!
      $bopId: ID!
      $includeBop: Boolean!
    ) {
      product {
        product(id: $productId) @include(if: $includeProduct) {
          id
          name
        }
        productConfiguration(id: $configId) @include(if: $includeConfig) {
          id
          name
        }
        bop(id: $bopId) @include(if: $includeBop) {
          id
          version
          versionStatus
          site {
            id
            name
          }
          nodes {
            module {
              id
              name
            }
          }
        }
      }
    }
  `,
  () => ({
    productId: productId.value || '',
    includeProduct: !!productId.value,
    configId: configId.value || '',
    includeConfig: !!configId.value,
    bopId: bopId.value || '',
    includeBop: !!bopId.value,
  }),
  () => ({
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    returnPartialData: true,
    keepPreviousResult: true,
    enabled: !!productId.value,
  }),
)

const bop = computed(() => fetchQuery.result.value?.product?.bop)

const productName = computed(
  () =>
    fetchQuery.result.value?.product?.product?.name ??
    cachedEntityName(client, 'Product', productId.value),
)
const configName = computed(
  () =>
    fetchQuery.result.value?.product?.productConfiguration?.name ??
    cachedEntityName(client, 'ProductConfiguration', configId.value),
)
const siteName = computed(
  () => bop.value?.site?.name ?? cachedEntityName(client, 'Site', siteId.value),
)
const bopVersion = computed(() =>
  bop.value?.version !== undefined && bop.value.versionStatus
    ? { version: bop.value.version, versionStatus: bop.value.versionStatus }
    : cachedBopVersion(client, bopId.value),
)

const configSelection = computed(() => {
  if (!configName.value) {
    return undefined
  }
  const parts = [configName.value]
  if (siteName.value) {
    parts.push(siteName.value)
  }
  if (bopVersion.value) {
    parts.push(
      `${bopVersion.value.version} ${t(`component.versionStatus.status.${bopVersion.value.versionStatus}`)}`,
    )
  }
  return parts.join(' - ')
})

const moduleOptions = computed(() =>
  itemSortByName(
    bop.value?.nodes
      ?.filter((n) => n.module?.name)
      .map((n) => ({ title: n.module.name, value: n.module.id })) || [],
    (i) => i.title,
  ),
)
const currentModuleName = computed(
  () =>
    bop.value?.nodes?.find((n) => n.module?.id === moduleId.value)?.module.name ??
    cachedEntityName(client, 'Module', moduleId.value),
)

const { currentProductionBatch, currentProductionBatchId } = useCurrentProductionBatch()

const batchForRoute = computed(() =>
  currentProductionBatch.value?.module.id === moduleId.value
    ? currentProductionBatch.value
    : undefined,
)
const batchPending = computed(
  () => !!currentProductionBatchId.value && !currentProductionBatch.value,
)

const batchSelection = computed(() => {
  const parts = [productName.value, configName.value, siteName.value]
  if (bopVersion.value) {
    parts.push(
      `${bopVersion.value.version} ${t(`component.versionStatus.status.${bopVersion.value.versionStatus}`)}`,
    )
  }
  parts.push(currentModuleName.value)
  const present = parts.filter((p): p is string => !!p)
  return present.length ? present.join(' - ') : undefined
})

const items = computed<BreadcrumbItem[]>(() => {
  if (route.name === 'operatorModule' && batchForRoute.value) {
    return [
      { pageLabel: t('section.operations'), to: { name: 'operatorHome' } },
      { pageLabel: batchForRoute.value.poNumber, selection: batchSelection.value },
    ]
  }

  const trail: BreadcrumbItem[] = [
    {
      pageLabel: t('section.operations'),
      to: { name: 'operatorHome' },
      selection: productName.value,
    },
  ]

  if (route.name === 'operatorProductConfigurations') {
    trail.push({ pageLabel: t('entity.productConfiguration.singular') })
    return trail
  }
  trail.push({
    pageLabel: t('entity.productConfiguration.singular'),
    to: { name: 'operatorProductConfigurations' },
    selection: configSelection.value,
  })

  if (route.name === 'operatorModule') {
    trail.push({
      pageLabel: currentModuleName.value || t('entity.module.singular'),
      dropdown: batchPending.value
        ? undefined
        : {
            options: moduleOptions.value,
            activeId: moduleId.value,
            loading: fetchQuery.loading.value,
            onSelect: (id) =>
              router.push({ name: 'operatorModule', params: { moduleId: id, stepId: undefined } }),
          },
    })
  }
  return trail
})
</script>

<template>
  <breadcrumb-trail class="operator-breadcrumb" :items="items" />
</template>

<style scoped lang="scss">
.operator-breadcrumb {
  clear: left;
}
</style>
